import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';

import { useTranslate } from 'src/locales';
import Iconify from 'src/components/iconify';
import CustomPopover, { usePopover } from 'src/components/custom-popover';

// ----------------------------------------------------------------------

export interface DataListAction {
  key: string;
  label: string;
  icon?: string;
  onClick: () => void;
  /** Rendered but non-interactive (e.g. bulk actions with no rows selected). */
  disabled?: boolean;
  /** Omitted entirely (e.g. tenant-gated actions). */
  hidden?: boolean;
  color?: string;
}

/**
 * The consolidated "Actions ▾" toolbar dropdown for the shared lists (JT1-1528). Restores the single
 * menu the old lists had (create / import / invite / bulk actions) rather than a row of separate
 * buttons, and keeps candidates/contacts/companies consistent. Bulk actions pass `disabled` so they
 * show but are inert until rows are selected.
 */
export default function DataListActionsMenu({ actions, label }: { actions: DataListAction[]; label?: string }) {
  const { t } = useTranslate();
  const popover = usePopover();

  const visible = actions.filter((a) => !a.hidden);
  if (!visible.length) return null;

  return (
    <>
      <Button
        variant="contained"
        size="small"
        endIcon={<Iconify icon="eva:chevron-down-fill" />}
        onClick={popover.onOpen}
      >
        {label ?? t('common.actions', { defaultValue: 'Actions' })}
      </Button>

      <CustomPopover open={popover.open} onClose={popover.onClose} sx={{ width: 200 }}>
        {visible.map((action) => (
          <MenuItem
            key={action.key}
            disabled={action.disabled}
            sx={action.color ? { color: action.color } : undefined}
            onClick={() => {
              popover.onClose();
              action.onClick();
            }}
          >
            {action.icon && <Iconify icon={action.icon} />}
            <Typography variant="body2">{action.label}</Typography>
          </MenuItem>
        ))}
      </CustomPopover>
    </>
  );
}
